import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import DataTable from "@/components/DataTable";
import { Factory, AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";

const PHASE_COLORS = {
  Design: "bg-slate-100 text-slate-700 border-slate-200",
  BOM: "bg-purple-50 text-purple-700 border-purple-200",
  Procurement: "bg-blue-50 text-blue-800 border-blue-200",
  Machining: "bg-amber-50 text-amber-800 border-amber-200",
  Assembly: "bg-orange-50 text-orange-800 border-orange-200",
  Testing: "bg-cyan-50 text-cyan-800 border-cyan-200",
  FIR: "bg-indigo-50 text-indigo-800 border-indigo-200",
  Dispatch: "bg-teal-50 text-teal-800 border-teal-200",
  "Ready for Dispatch": "bg-teal-50 text-teal-800 border-teal-200",
  Dispatched: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Received: "bg-rose-50 text-rose-700 border-rose-200",
};

function Chip({ status, className = "" }) {
  const cls = PHASE_COLORS[status] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase font-mono ${cls} ${className}`}>
      {status}
    </span>
  );
}

export default function LifecycleList() {
  const nav = useNavigate();
  const [widgets, setWidgets] = useState(null);

  useEffect(() => {
    api.get("/lifecycle/dashboard/widgets")
      .then((r) => setWidgets(r.data || null))
      .catch(() => setWidgets(null));
  }, []);

  const fetchData = React.useCallback(async ({ skip, limit, search, filters }) => {
    const params = { skip, limit, search };
    if (filters.phase) params.phase = filters.phase;
    if (filters.manufacturing_type) params.manufacturing_type = filters.manufacturing_type;
    if (filters.delayed_only === "true") params.delayed_only = true;
    try {
      const { data } = await api.get("/lifecycle/list/all", { params });
      return data || { items: [], total: 0 };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[LifecycleList] /lifecycle/list/all failed:", err?.response?.status);
      return { items: [], total: 0 };
    }
  }, []);

  const columns = [
    { key: "cylinder_number", label: "Cyl #",
      render: (r) => <span className="font-mono text-[13px] font-bold text-blue-900">#{r.cylinder_number}</span> },
    { key: "customer_name", label: "Customer", className: "text-slate-800" },
    { key: "manufacturing_type", label: "Type",
      render: (r) => (
        <span className={`text-[10px] uppercase font-semibold font-mono px-1.5 py-0.5 rounded border ${
          r.manufacturing_type === "Reconditioning"
            ? "bg-rose-50 text-rose-700 border-rose-200"
            : "bg-blue-50 text-blue-800 border-blue-200"}`}>
          {r.manufacturing_type === "Reconditioning" ? "Recon" : "New"}
        </span>
      ) },
    { key: "current_phase", label: "Current Phase",
      render: (r) => <Chip status={r.current_phase} /> },
    { key: "bom_status", label: "BOM", render: (r) => <span className="text-[11px] text-slate-600">{r.bom_status}</span> },
    { key: "tube_status", label: "Tube", render: (r) => <span className="text-[11px] text-slate-600">{r.tube_status}</span> },
    { key: "rod_status", label: "Rod", render: (r) => <span className="text-[11px] text-slate-600">{r.rod_status}</span> },
    { key: "assembly_status", label: "Assy", render: (r) => <span className="text-[11px] text-slate-600">{r.assembly_status}</span> },
    { key: "testing_status", label: "Test", render: (r) => <span className="text-[11px] text-slate-600">{r.testing_status}</span> },
    { key: "fir_done", label: "FIR",
      render: (r) => <span className={`text-[10px] font-mono font-bold ${r.fir_done ? "text-emerald-700" : "text-slate-400"}`}>
        {r.fir_done ? "DONE" : "—"}
      </span> },
    { key: "dispatch_status", label: "Dispatch", render: (r) => <Chip status={r.dispatch_status} /> },
    { key: "delay_count", label: "Delay",
      render: (r) => r.delay_count > 0
        ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded font-mono">
            <AlertTriangle className="w-3 h-3" /> {r.delay_count}
          </span>
        : <span className="text-slate-300 font-mono text-xs">—</span> },
  ];

  return (
    <div data-testid="lifecycle-list-page" className="space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">Manufacturing · Module 5</div>
        <h1 className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>Lifecycle Tracker</h1>
        <p className="text-sm text-slate-500 mt-1">
          Track every cylinder from Engineering release through Dispatch. Two workflows: New Manufacturing & Reconditioning.
        </p>
      </div>

      {widgets && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            ["Design Pending", widgets.widgets.design_pending, "text-slate-700"],
            ["BOM Pending", widgets.widgets.bom_pending, "text-purple-700"],
            ["Material Pending", widgets.widgets.material_pending, "text-blue-800"],
            ["Tube Machining", widgets.widgets.tube_machining_running, "text-amber-700"],
            ["Rod Machining", widgets.widgets.piston_rod_machining_running, "text-amber-700"],
            ["Assembly", widgets.widgets.assembly_running, "text-orange-700"],
            ["Testing", widgets.widgets.testing_running, "text-cyan-700"],
            ["FIR Pending", widgets.widgets.fir_pending, "text-indigo-700"],
            ["Ready", widgets.widgets.ready_for_dispatch, "text-teal-700"],
            ["Dispatched Today", widgets.widgets.dispatched_today, "text-emerald-700"],
            ["Delayed", widgets.widgets.delayed_cylinders, "text-red-700"],
          ].map(([label, value, cls]) => (
            <Card key={label} data-testid={`lc-widget-${label.toLowerCase().replace(/\s/g, "-")}`}
                  className="bg-white border border-slate-200 rounded p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
              <div className={`mt-1 font-mono text-2xl font-bold ${cls}`}>{value}</div>
            </Card>
          ))}
        </div>
      )}

      <DataTable
        columns={columns}
        fetchData={fetchData}
        filters={[
          { key: "phase", label: "Phase",
            options: ["Design", "BOM", "Procurement", "Machining", "Assembly", "Testing", "FIR", "Ready for Dispatch", "Dispatched"].map((s) => ({ value: s, label: s })) },
          { key: "manufacturing_type", label: "Type",
            options: [{ value: "New", label: "New Manufacturing" }, { value: "Reconditioning", label: "Reconditioning" }] },
          { key: "delayed_only", label: "Delayed",
            options: [{ value: "true", label: "Only Delayed" }] },
        ]}
        storageKey="lifecycle-list"
        onRowClick={(r) => nav(`/lifecycle/${r.id}`)}
        emptyMessage="No cylinders in lifecycle tracking yet."
      />
    </div>
  );
}
